import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getWeatherByCoordinates,
  getWeatherForecast,
  WeatherData,
  WeatherForecast,
} from "../../services/weather";

interface WeatherState {
  current: WeatherData | null;
  forecast: WeatherData[];
  isLoading: boolean;
  error: string | null;
}

const initialState: WeatherState = {
  current: null,
  forecast: [],
  isLoading: false,
  error: null,
};

export const fetchWeatherByCoordinates = createAsyncThunk(
  "weather/fetchByCoordinates",
  async (coordinates: { lat: number; lon: number }) => {
    const { lat, lon } = coordinates;
    const weatherData = await getWeatherByCoordinates(lat, lon);
    return weatherData;
  }
);

export const fetchWeatherForecast = createAsyncThunk(
  "weather/fetchForecast",
  async (coordinates: { lat: number; lon: number }) => {
    const { lat, lon } = coordinates;
    const forecast = await getWeatherForecast(lat, lon);
    return forecast;
  }
);

export const weatherSlice = createSlice({
  name: "weather",
  initialState,
  reducers: {
    clearWeather: (state) => {
      state.current = null;
      state.forecast = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWeatherByCoordinates.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchWeatherByCoordinates.fulfilled,
        (state, action: PayloadAction<WeatherData>) => {
          state.isLoading = false;
          state.current = action.payload;
        }
      )
      .addCase(fetchWeatherByCoordinates.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to fetch weather";
      })
      .addCase(fetchWeatherForecast.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchWeatherForecast.fulfilled,
        (state, action: PayloadAction<WeatherForecast>) => {
          state.isLoading = false;
          state.current = action.payload.current;
          state.forecast = action.payload.forecast;
        }
      )
      .addCase(fetchWeatherForecast.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.error.message || "Failed to fetch weather forecast";
      });
  },
});

export const { clearWeather } = weatherSlice.actions;

export default weatherSlice.reducer;
